import '../../base/logs_inspector/logs_inspector_modal/index.js';
import type {
  Cmd,
  ImmutableProgram,
  ImmutableResult,
  InitialProperties,
  UpdateHandler,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  makeVariants,
  Variant,
} from '#lit/components/mixins/immutable_component/index.js';
import {when} from 'lit/directives/when.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type AudienceExportActivitiesTable from './index.js';
import {exportService} from '#lit/services/exports.js';
import {createDiscriminator} from '#lit/utils/discriminator.js';
import type {HTTPResponse} from '#lit/lib/http/immutable_fetcher.js';
import {wrapPromise} from '#lit/lib/http/immutable_fetcher.js';
import {fold} from '#lit/types/Either.js';
import {AsyncValueStatus} from '#lit/types/async.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import type {CustomInputEvent} from '#lit/components/base/input/index.js';
import {Variant as FlyInputVariant} from '#lit/components/base/input/index.js';
import {exportActivitiesLocale} from '#lit/common/locale/export_activities.js';
import {renderNotification} from '#lit/utils/notifications.js';
import {AlertIntent, AlertSize} from '#lit/components/base/alert/index.js';
import copy from 'copy-to-clipboard';
import type {CustomSelectEvent} from '#lit/components/base/select/index.js';
import {SelectVariant} from '#lit/components/base/select/index.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import type {Nullable} from '#lit/types/general.js';
import {getQueryParameter} from '#lit/utils/http.js';
import {formatTimestamp} from '#utils/dates.js';
import type {Metadata} from '#lit/types/signal_routes.js';
import {
  addParamToCurrentUrl,
  removeParamFromCurrentUrl,
} from '#lit/utils/url.js';
import {fetchAudienceExportRunLogs} from './utils.js';
import type {
  AudienceExportActivitiesResponse,
  AudienceExportActivity,
} from '#lit/types/audience_exports.js';
import {ExportStateUI, TABLE_STATES} from '#lit/types/audience_exports.js';
import {ActivityHeaders} from './constants.js';
import {audiencesLocale} from '#lit/common/locale/audiences.js';
import {locale} from '#lit/common/locale/index.js';

const activitiesTableLocale =
  exportActivitiesLocale[locale.current].activitiesTable;

export const SKELETON_ROW_COUNT = 5;

const DEFAULT_PAGE_LIMIT = 12;

const DEFAULT_FILTERED_DATA: AudienceExportActivity[] = [];
const DEFAULT_PAGE = 0;
const DEFAULT_QUERY = '';
const BLANK = '-';

const audienceExportsLocale = audiencesLocale[locale.current].exportActivities;

export type Model = {
  tableData: AudienceExportActivity[];
  filteredData: AudienceExportActivity[];
  page: number;
  pageLimit: number;
  query: string;
  statuses: string[];
  destinationNames: string[];
  state: TABLE_STATES;
  filters: FilterStore;
  errors: string[];
  status: AsyncValueStatus;
  currentExportActivity: Nullable<AudienceExportActivity>;
  modal: {
    open: boolean;
    label: string;
  };
};

export type Msg = VariantsFor<typeof Msg>;

export const Msg = makeVariants({
  setSearchQuery: Variant<string>()('set-search-query'),
  setStatusFilter: Variant<string>()('set-status-filter'),
  setDestinationFilter: Variant<string>()('set-destination-filter'),
  setFilteredData: Variant<AudienceExportActivity[]>()('set-filtered-data'),
  goToNextPage: Variant<void>()('go-to-next-page'),
  goToPrevPage: Variant<void>()('go-to-prev-page'),
  setPage: Variant<number>()('set-page'),
  setTableState: Variant<TABLE_STATES>()('set-table-state'),
  openModal: Variant<void>()('open-modal'),
  closeModal: Variant<void>()('close-modal'),
  handleExportActivitiesResponse: Variant<
    HTTPResponse<AudienceExportActivitiesResponse>
  >()('handle-activities-response'),
  setCurrentExportActivity: Variant<Nullable<AudienceExportActivity>>()(
    'set-current-export-activity'
  ),
});

type FilterStore = {
  status?: string;
  destination_name?: string;
  audience?: string;
};

const hasErrors = (activity: AudienceExportActivity): boolean => {
  return (
    activity.rejected_records > 0 ||
    (activity.error_file_uri &&
      activity.status.state === ExportStateUI.ExportFailed) ||
    activity.status.state === ExportStateUI.ProcessingFailed
  );
};

function totalPages(tableData: AudienceExportActivity[], pageLimit: number) {
  return Math.ceil(tableData.length / pageLimit);
}

function paginator(direction: 'prev' | 'next') {
  return function (host: AudienceExportActivitiesTable) {
    const currentPage = host.model.page;
    let targetPage = direction === 'next' ? currentPage + 1 : currentPage - 1;

    targetPage = Math.min(
      Math.max(targetPage, 0),
      totalPages(host.model.tableData, host.model.pageLimit)
    );

    host.handleUpdate(Msg.setPage(targetPage));
  };
}

const discriminate = createDiscriminator<
  HTTPResponse<AudienceExportActivitiesResponse>,
  Model
>()('kind');

function fetchAudienceExportActivities(host: AudienceExportActivitiesTable) {
  return wrapPromise<AudienceExportActivitiesResponse>(host, {
    promise: exportService.getAudienceExportActivities(
      encodeURIComponent(host.params.id ?? host.params.audience_id)
    ),
    handler: Msg.handleExportActivitiesResponse,
  });
}

function openSelectedExportRunIDModal(host: AudienceExportActivitiesTable) {
  const exportRunID = getQueryParameter('export_run_id');
  if (!exportRunID) return;

  const currentExportActivity = host.model.tableData.find(
    ({export_id}: AudienceExportActivity) => export_id.value === exportRunID
  );
  if (currentExportActivity) {
    selectExportActivityAndOpenModal(currentExportActivity, host.handleUpdate);
  }
}

function init(_props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const tableData: AudienceExportActivity[] = [];
  const page = DEFAULT_PAGE;
  const pageLimit = DEFAULT_PAGE_LIMIT;
  const filteredData = DEFAULT_FILTERED_DATA;
  const query = DEFAULT_QUERY;
  const modal = {
    open: false,
    label: audienceExportsLocale.modal.titleLabel,
  };

  return [
    {
      state: TABLE_STATES.LOADING,
      tableData: tableData,
      page: page,
      pageLimit: pageLimit,
      filteredData: filteredData,
      query: query,
      filters: {},
      statuses: [],
      destinationNames: [],
      errors: [],
      status: AsyncValueStatus.IDLE,
      modal,
      currentExportActivity: null,
    },
    cmd(fetchAudienceExportActivities),
  ];
}

function update(
  this: AudienceExportActivitiesTable,
  msg: Msg,
  model: Model,
  cmd: Cmd
): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'go-to-next-page':
      return [model, cmd(paginator('next'))] as const;
    case 'go-to-prev-page':
      return [model, cmd(paginator('prev'))] as const;
    case 'set-page':
      return {...model, page: msg.value} as const;
    case 'set-table-state':
      return {...model, state: msg.value} as const;
    case 'set-search-query':
      return {...model, query: msg.value} as const;
    case 'set-status-filter': {
      const newFilters = model.filters;
      newFilters.status = msg.value;
      return [{...model, filters: newFilters}, cmd(() => this.updateResults())];
    }
    case 'set-destination-filter': {
      const newFilters = model.filters;
      newFilters.destination_name = msg.value;

      return [{...model, filters: newFilters}, cmd(() => this.updateResults())];
    }
    case 'set-filtered-data': {
      return [
        {...model, filteredData: msg.value},
        cmd(host => host.handleUpdate(Msg.setPage(1))),
      ];
    }
    case 'open-modal':
      return {...model, modal: {...model.modal, open: true}} as const;
    case 'close-modal':
      return {...model, modal: {...model.modal, open: false}} as const;
    case 'set-current-export-activity':
      return {...model, currentExportActivity: msg.value} as const;
    case 'handle-activities-response':
      return [
        discriminate(msg.value)({
          pending: () => ({...model, state: TABLE_STATES.LOADING}),
          complete: ({response}) =>
            fold(
              (err: Error) => {
                return {
                  ...model,
                  errors: [...model.errors, err.message],
                  state: TABLE_STATES.ERROR,
                };
              },
              (data: AudienceExportActivitiesResponse) => {
                const tableData = formatTableData(data);
                const destinationNames = [
                  ...new Set(tableData.map(v => v.destination_name)),
                ];
                const statuses = [
                  ...new Set(tableData.map(v => v.status.state)),
                ];
                statuses.push(ExportStateUI.NotEmpty);
                return {
                  ...model,
                  state: TABLE_STATES.LOADED,
                  tableData: tableData,
                  statuses: statuses,
                  destinationNames: destinationNames,
                };
              }
            )(response),
        }),
        cmd((host: AudienceExportActivitiesTable) => {
          openSelectedExportRunIDModal(host);
          host.handleUpdate(Msg.setStatusFilter(ExportStateUI.NotEmpty));
        }),
      ];
  }
}

function formatTableData(
  response: AudienceExportActivitiesResponse
): AudienceExportActivity[] {
  const transformed = response.export_activities.map(activity => ({
    status: activity.export_state,
    export_time: formatTimestamp(activity.time),
    added: activity.added,
    removed: activity.removed,
    rejected: activity.rejected,
    successful_records: activity.successfull_exports,
    rejected_records: activity.failed_exports,
    export_id: activity.export_run_id,
    destination_name: activity.destination_name,
    error_file_uri: activity.error_file_uri,
    audience_name: activity.audience_name,
    platform: activity.platform,
  }));
  return transformed;
}

function renderFilters(host: AudienceExportActivitiesTable, model: Model) {
  return html`
    <fly-select
      placeholder="Destination"
      ?disabled=${model.state === TABLE_STATES.LOADING ||
      model.tableData.length === 0}
      variant=${SelectVariant.CONDENSED}
      @selected=${(e: CustomEvent<CustomSelectEvent>) => {
        host.handleUpdate(Msg.setDestinationFilter(e.detail.value));
        host.updateResults();
      }}
      searchable
    >
      ${Object.values(model.destinationNames).map(destinationName => {
        return html`
          <fly-list-item value="${destinationName}">
            ${destinationName}
          </fly-list-item>
        `;
      })}
    </fly-select>

    <fly-select
      placeholder="Status"
      ?disabled=${model.state === TABLE_STATES.LOADING ||
      model.tableData.length === 0}
      variant=${SelectVariant.CONDENSED}
      initial=${ExportStateUI.NotEmpty}
      @selected=${(e: CustomEvent<CustomSelectEvent>) => {
        host.handleUpdate(Msg.setStatusFilter(e.detail.value));
      }}
    >
      ${repeat(
        Object.values(model.statuses),
        status => status,
        status => html`
          <fly-list-item value="${status}">${status}</fly-list-item>
        `
      )}
    </fly-select>
  `;
}

function renderHeader() {
  return html`
    <thead>
      <tr class="fly-table__columns">
        ${repeat(
          ActivityHeaders,
          header => header.label,
          header => html`
            <th>
              <div class="fly-table__column fly-table__cell">
                ${header.label}
                ${when(
                  header.tooltip,
                  () =>
                    html`<fly-tooltip>
                      <p slot="content" class="activities-show-table__tooltip">
                        ${header.tooltip}
                      </p>
                      <fly-icon size="sm" icon="info_outlined"></fly-icon>
                    </fly-tooltip>`
                )}
              </div>
            </th>
          `
        )}
      </tr>
    </thead>
  `;
}

function renderRow(
  activity: AudienceExportActivity,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  return html`
    <tr class="fly-table__row activities-show-table__row">
      ${renderCell(activity.destination_name, model)}
      ${renderStatusCell(activity, model, handleUpdate)}
      ${renderCell(activity.export_time, model)}
      ${renderCell(activity.added, model)}
      ${renderCell(activity.removed, model)}
      ${renderCell(activity.rejected_records, model)}
      ${renderExportIdCell(activity, model)}
      ${renderShowErrorDetailsCell(activity, model, handleUpdate)}
    </tr>
  `;
}

//FIXME(Charlie) - check if table-cell should always be there
function renderCell(cellInfo: string | number, model: Model) {
  const classes = {
    'fly-table__cell__loading': model.state === TABLE_STATES.LOADING,
  };

  return html`
    <td class=${classMap(classes)}>
      ${model.state === TABLE_STATES.LOADING
        ? html`<fly-bar-loader
            width="100px"
            height="30px"
            animated
          ></fly-bar-loader>`
        : html`<div class="fly-table__cell">${cellInfo}</div>`}
    </td>
  `;
}

function renderActivityStateText(activity: AudienceExportActivity) {
  const activityState = when(
    (activity.error_file_uri &&
      activity.status.state === ExportStateUI.ExportFailed) ||
      activity.status.state === ExportStateUI.ProcessingFailed,
    () =>
      html`<span class="fly-table__cell__export-failed"
        >${activity.status.state}</span
      >`,
    () => html`<span>${activity.status.state}</span>`
  );

  return when(
    activity.platform === 'Live Ramp Activation' &&
      activity.status.state === ExportStateUI.Processing,
    () => html`
      <fly-tooltip
        content="${activitiesTableLocale.liveRampSftpTooltip}"
        placement="right"
      >
        ${activityState}
      </fly-tooltip>
    `,
    () => activityState
  );
}

function renderStatusCell(
  activity: AudienceExportActivity,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  const classes = {
    'fly-table__cell__loading': model.state === TABLE_STATES.LOADING,
  };

  return html`
    <td class=${classMap(classes)}>
      ${model.state === TABLE_STATES.LOADING
        ? html`<fly-bar-loader
            width="100px"
            height="30px"
            animated
          ></fly-bar-loader>`
        : html`
            <div
              @click=${() => {
                if (hasErrors(activity)) {
                  selectExportActivityAndOpenModal(activity, handleUpdate);
                }
              }}
              class="fly-table__cell"
            >
              ${renderActivityStateText(activity)}
            </div>
          `}
    </td>
  `;
}

function renderCopyableExportID(exportIdLong: string, exportIdShort?: string) {
  return html`
    <fly-tooltip content=${exportIdLong}>
      <div
        class="fly-table__cell activities-show-table__export-id"
        @click=${(e: Event) => {
          e.stopPropagation();
          renderNotification(
            `Copied ${exportIdLong} to clipboard`,
            AlertIntent.PRIMARY,
            AlertSize.MD
          );
          copy(exportIdLong);
        }}
      >
        <div>${exportIdShort ? exportIdShort : exportIdLong}</div>
        <fly-icon
          class="activities-show-table__fly-icon"
          icon="content_copy"
          size="xs"
        ></fly-icon>
      </div>
    </fly-tooltip>
  `;
}

function renderExportIdCell(activity: AudienceExportActivity, model: Model) {
  const classes = {
    'fly-table__cell__loading': model.state === TABLE_STATES.LOADING,
  };

  return html`
    <td class=${classMap(classes)}>
      ${model.state === TABLE_STATES.LOADING
        ? html`<fly-bar-loader
            width="100px"
            height="30px"
            animated
          ></fly-bar-loader>`
        : renderCopyableExportID(
            activity.export_id.value,
            activity.export_id.short_version
          )}
    </td>
  `;
}

const renderShowErrorDetailsCell = (
  activity: AudienceExportActivity,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) => {
  const classes = {
    'fly-table__cell__loading': model.state === TABLE_STATES.LOADING,
  };

  return html`
    <td class=${classMap(classes)}>
      ${model.state === TABLE_STATES.LOADING
        ? html`<fly-bar-loader
            width="100%"
            height="30px"
            animated
          ></fly-bar-loader>`
        : renderShowErrorDetailsButton(activity, handleUpdate)}
    </td>
  `;
};

function renderShowErrorDetailsButton(
  activity: AudienceExportActivity,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  return hasErrors(activity)
    ? html`<fly-button
        intent="primary"
        size="xs"
        @click=${() => {
          selectExportActivityAndOpenModal(activity, handleUpdate);
        }}
      >
        Show Details
      </fly-button>`
    : BLANK;
}

function buildMetadata(
  activity: AudienceExportActivity,
  activityId: string | number
): Metadata {
  return [
    {
      label: audienceExportsLocale.modal.audienceIdLabel,
      value: activityId,
    },
    {
      label: audienceExportsLocale.modal.platformLabel,
      value: activity.platform,
      tooltip: audienceExportsLocale.modal.platformTooltip,
    },
    {
      label: audienceExportsLocale.modal.successfulRecordsLabel,
      value: activity.successful_records,
      tooltip: audienceExportsLocale.modal.successfulRecordsTooltip,
    },
    {
      label: audienceExportsLocale.modal.rejectedRecordsLabel,
      value: activity.rejected_records,
      tooltip: audienceExportsLocale.modal.rejectedRecordsTooltip,
    },
  ];
}

function buildModalHeader(
  activity: Nullable<AudienceExportActivity>,
  activityId: string | number
): TemplateResult<1> {
  if (!activity) return html``;
  return html`<div>
    <p>
      Export for <strong>${activity.audience_name}</strong> to
      <strong>${activity.platform}</strong> failed due to
      <strong>${activity.status.description}</strong>
    </p>

    <div class="metadata-grid">
      ${repeat(
        buildMetadata(activity, activityId),
        item => item.label,
        item => html`
          <div class="metadata-grid__item">
            <div class="metadata-grid__row">
              <span class="metadata-grid__row__label">${item.label}</span>
              ${item.tooltip
                ? html`
                    <fly-tooltip
                      class="metadata-grid__row__tooltip"
                      content=${item.tooltip}
                    >
                      <fly-icon size="sm" icon="info_outlined"></fly-icon>
                    </fly-tooltip>
                  `
                : ''}
            </div>
            <div class="metadata-grid__value">${item.value}</div>
          </div>
        `
      )}
    </div>
  </div>`;
}

function getModalHandlers(
  activity: Nullable<AudienceExportActivity>,
  activityId: string | number
) {
  if (!activity) return {};
  return {
    fetchLogs: () => fetchAudienceExportRunLogs(activity, activityId),
  };
}

function selectExportActivityAndOpenModal(
  activity: AudienceExportActivity,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  handleUpdate(Msg.setCurrentExportActivity(activity));
  handleUpdate(Msg.openModal());
  addParamToCurrentUrl('export_run_id', activity.export_id.value);
}

function deselectExportActivityAndCloseModal(
  handleUpdate: UpdateHandler<Model, Msg>
) {
  handleUpdate(Msg.setCurrentExportActivity(null));
  handleUpdate(Msg.closeModal());
  removeParamFromCurrentUrl('export_run_id');
}

// FIXME(Charlie) how do you get key back
function renderActivitiesTable(
  host: AudienceExportActivitiesTable,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  const activityId = host.params.audience_id ?? host.params.id;
  return html`
    ${renderErrors(model)}
    <logs-inspector-modal
      label=${model.modal.label}
      ?open=${model.modal.open}
      .header=${buildModalHeader(model.currentExportActivity, activityId)}
      .handlers=${getModalHandlers(model.currentExportActivity, activityId)}
      @closed=${() => deselectExportActivityAndCloseModal(handleUpdate)}
    ></logs-inspector-modal>
    <div class="fly-table-container">
      <div
        class="fly-table-container__controls
            fly-table-container__controls--padded data-table__header"
      >
        <fly-input
          hasicon
          class="activities-show-table__text-input"
          ?disabled="${model.state === TABLE_STATES.LOADING ||
          model.tableData.length === 0}"
          type="search"
          placeholder="Search"
          variant=${FlyInputVariant.BORDERLESS_NO_BG}
          @input=${(e: CustomEvent<CustomInputEvent>) => {
            host.handleUpdate(Msg.setSearchQuery(e.detail.value));
            host.updateResults();
          }}
        >
          <div slot="icon">
            <fly-icon icon="search"></fly-icon>
          </div>
        </fly-input>
        <div class="activities-show-table__controls__filters">
          ${renderFilters(host, model)}
        </div>
        <slot name="header"></slot>
      </div>
      <div class="fly-table">
        <table>
          ${renderHeader()}
          <tbody>
            ${host.pagedData.map(activity =>
              renderRow(activity, model, handleUpdate)
            )}
          </tbody>
        </table>
      </div>
      <div class="fly-table-container__controls data-table__footer">
        <fly-paginator
          .page=${model.page}
          total=${totalPages(model.tableData, model.pageLimit)}
          @previous=${() => host.handleUpdate(Msg.goToPrevPage())}
          @next=${() => host.handleUpdate(Msg.goToNextPage())}
        >
        </fly-paginator>
      </div>
    </div>
  `;
}

function skeletonHeaderMarkup() {
  return html`
    <thead>
      <tr class="fly-table__columns">
        <th>
          <div class="fly-table__column  fly-table__cell">
            <fly-bar-loader width="80px"></fly-bar-loader>
          </div>
        </th>
        <th>
          <div class="fly-table__column  fly-table__cell">
            <fly-bar-loader width="120px"></fly-bar-loader>
          </div>
        </th>
      </tr>
    </thead>
  `;
}

function skeletonRowMarkup() {
  return html`
    <tr class="fly-table__row">
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
      <td>
        <div class="fly-table__cell">
          <fly-bar-loader width="100px" height="30px"></fly-bar-loader>
        </div>
      </td>
    </tr>
  `;
}

function renderErrors(model: Model) {
  if (model.errors.length === 0) return html``;
  return html`
    <div class="fly-table__errors-container">
      <fly-announcement intent="danger">
        <ul>
          ${model.errors.map(error => html`<li>${error}</li>`)}
        </ul>
      </fly-announcement>
    </div>
  `;
}

function skeletonTable(model: Model) {
  const rows = new Array(SKELETON_ROW_COUNT).fill(0);
  return html`
    ${renderErrors(model)}
    <div class="fly-table-container activities-show-table--skeleton-container">
      <div
        class="fly-table-container__controls
          fly-table-container__controls--padded"
      >
        <fly-bar-loader height="30px" width="300px"></fly-bar-loader>
      </div>
      <div class="fly-table fly-table--rounded fly-table--not-hoverable">
        <table class="activities-show-table--skeleton__table">
          ${skeletonHeaderMarkup()}
          <tbody>
            ${rows.map(() => {
              return skeletonRowMarkup();
            })}
          </tbody>
        </table>
      </div>
      <div class="activities-show-table--skeleton__callout">
        <fly-call-out
          headline=${exportActivitiesLocale.en.activitiesTable.noMatches
            .headline}
          caption=${exportActivitiesLocale.en.activitiesTable.noMatches.caption}
        >
        </fly-call-out>
      </div>
    </div>
  `;
}

function view(
  this: AudienceExportActivitiesTable,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  if (
    this.model.tableData.length === 0 &&
    this.model.state !== TABLE_STATES.LOADING
  ) {
    return skeletonTable(model);
  } else {
    return renderActivitiesTable(this, model, handleUpdate);
  }
}

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
  extendsAdapter: {
    page: {
      resolver: model => model.page,
      variant: Msg.setPage,
    },
    query: {
      resolver: model => model.query,
      variant: Msg.setSearchQuery,
    },
    filteredData: {
      resolver: model => model.filteredData,
      variant: Msg.setFilteredData,
    },
    tableData: {
      resolver: model => model.tableData,
      variant: Msg.handleExportActivitiesResponse,
    },
  },
};
